import { useCallback, useEffect, useRef, useState } from "react";

import type {
  BannerLibraryFilterType,
  LabelOption,
  LoadBannerFilterOptions,
  LoadBannerPage,
  MarketingMaterial,
} from "./types";

const mergeUnique = (
  prev: LabelOption[],
  next: LabelOption[],
): LabelOption[] => {
  const byValue = new Map(prev.map((o) => [o.value, o]));
  for (const o of next) if (!byValue.has(o.value)) byValue.set(o.value, o);
  return [...byValue.values()];
};

/**
 * Manual infinite-scroll loader — a React-Query-free replacement so the shared
 * BannerLibrary works in apps without React Query (backoffice). Each app injects
 * a stable `loadPage`. Re-fetches page 1 whenever the filters change.
 */
export const useBannerLibraryData = ({
  loadPage,
  loadFilterOptions,
  type,
  languages,
  sizes,
}: {
  loadPage: LoadBannerPage;
  loadFilterOptions?: LoadBannerFilterOptions;
  type: BannerLibraryFilterType;
  languages: string[];
  sizes: string[];
}) => {
  const [items, setItems] = useState<MarketingMaterial[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [status, setStatus] = useState<"pending" | "error" | "success">(
    "pending",
  );
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [languageOptions, setLanguageOptions] = useState<LabelOption[]>([]);
  const [sizeOptions, setSizeOptions] = useState<LabelOption[]>([]);

  // Serialise the filters so the load effect doesn't re-run on every render
  // from new array references.
  const filterKey = JSON.stringify({ type, languages, sizes });

  // Hold the latest filters in a ref so fetchNextPage stays stable yet always
  // requests the currently-applied filters.
  const filtersRef = useRef({ type, languages, sizes });
  filtersRef.current = { type, languages, sizes };

  // When an app supplies a dedicated filter-options loader, those options are
  // the source of truth and the page-derived ones are ignored.
  const hasFilterOptionsLoader = loadFilterOptions != null;

  const loadFirstPage = useCallback(async () => {
    setStatus("pending");
    setIsFetchingNextPage(false);
    try {
      const { type: t, languages: l, sizes: s } = filtersRef.current;
      const result = await loadPage({ type: t, languages: l, sizes: s, page: 1 });
      setItems(result.items);
      setNextPage(result.nextPage);
      if (!hasFilterOptionsLoader) {
        setLanguageOptions(result.availableLanguageOptions ?? []);
        setSizeOptions(result.availableSizeOptions ?? []);
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }, [loadPage, hasFilterOptionsLoader]);

  useEffect(() => {
    void loadFirstPage();
    // filterKey captures type/languages/sizes; loadPage must be stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFirstPage, filterKey]);

  // Fetch the complete filter options once on mount (when a loader is provided),
  // independent of pagination and the currently-applied filters.
  useEffect(() => {
    if (!loadFilterOptions) return;
    let cancelled = false;
    void (async () => {
      try {
        const { languageOptions: l, sizeOptions: s } = await loadFilterOptions();
        if (cancelled) return;
        setLanguageOptions(l);
        setSizeOptions(s);
      } catch {
        // Non-fatal: leave options empty rather than breaking the grid.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadFilterOptions]);

  const fetchNextPage = useCallback(async () => {
    if (nextPage == null || isFetchingNextPage) return;
    setIsFetchingNextPage(true);
    try {
      const { type: t, languages: l, sizes: s } = filtersRef.current;
      const result = await loadPage({
        type: t,
        languages: l,
        sizes: s,
        page: nextPage,
      });
      setItems((prev) => [...prev, ...result.items]);
      setNextPage(result.nextPage);
      if (!hasFilterOptionsLoader) {
        setLanguageOptions((prev) =>
          mergeUnique(prev, result.availableLanguageOptions ?? []),
        );
        setSizeOptions((prev) =>
          mergeUnique(prev, result.availableSizeOptions ?? []),
        );
      }
    } catch {
      // Keep prior items; a transient next-page failure shouldn't wipe the grid.
    } finally {
      setIsFetchingNextPage(false);
    }
  }, [loadPage, nextPage, isFetchingNextPage, hasFilterOptionsLoader]);

  return {
    items,
    status,
    hasNextPage: nextPage != null,
    isFetchingNextPage,
    fetchNextPage,
    refetch: loadFirstPage,
    languageOptions,
    sizeOptions,
  };
};
